import React, { useState } from 'react';
import { Nullable } from 'global';
import { logError } from 'utils/logger';
import { useNotifications } from 'modules/notification';
import { NetworkStatus, useLazyQuery, useQuery } from '@apollo/client';
import { Checklist, Notes } from './attributesComponents';
import { useBPFMutations } from '../hooks/useBPFMutations';
import { UpdateBPFVariablesSchema } from '../model/schemas';
import { AttributeRenderers, FlowsState, ManagerApiContainerProps, TemplatesState } from './types';
import {
  flowsDataNormalizer,
  templatesDataNormalizer,
  departmentsDataNormalizer,
  updateBusinessProcessFlowsDtoNormalizer,
} from '../lib';
import {
  Stage,
  FlowId,
  StageId,
  stagesApi,
  Department,
  DepartmentId,
  TemplatesResponse,
  DepartmentsResponse,
  VOTemplatesAndStagesResponse,
  VOTemplatesAndStagesVariables,
  DepartmentsVariables,
  TemplatesVariables,
} from '../api';

export const VOStagesContainer = ({ children, id, accountId }: ManagerApiContainerProps) => {
  const { showNotification } = useNotifications();

  const [genericLoading, setGenericLoading] = useState<boolean>(true);

  const [selectedFlow, setFlow] = useState<Nullable<FlowId>>(null);
  const [selectedStage, setSelectedStage] = useState<Nullable<StageId>>(null);
  const [viewedStage, setViewedStage] = useState<Nullable<StageId>>(null);
  const [selectedDepartment, setDepartment] = useState<Nullable<DepartmentId>>(null);
  const [notConfirmedSelectedStage, setNotConfirmedSelectedStage] = useState<Nullable<StageId>>(null);

  const [flowsData, setFlowsData] = useState<FlowsState>({});
  const [templatesData, setTemplatesData] = useState<TemplatesState>({});
  const [departmentsData, setDepartmentsData] = useState<Department[]>([]);

  const { isBPFsUpdating, isStagesUpdating, onUpdateBusinessProcessFlows, handleUpdateStageAttributes } =
    useBPFMutations();

  const [fetchDepartments, { loading: isDepartmentsLoading }] = useLazyQuery<DepartmentsResponse, DepartmentsVariables>(
    stagesApi.fetchDepartments
  );

  const [fetchTemplates, { loading: isTemplatesLoading }] = useLazyQuery<TemplatesResponse, TemplatesVariables>(
    stagesApi.fetchTemplates
  );

  const {
    loading,
    networkStatus,
    data: VOTemplatesAndStagesResponse,
  } = useQuery<VOTemplatesAndStagesResponse, VOTemplatesAndStagesVariables>(stagesApi.fetchVOTemplatesAndStages, {
    variables: { id },
    notifyOnNetworkStatusChange: true,
    onCompleted({ vendorOpportunity: { businessProcessFlows } }) {
      if (!businessProcessFlows) {
        setGenericLoading(false);
        return;
      }

      if (!selectedFlow) {
        setFlow(businessProcessFlows.flows[0].businessProcessFlowId);
        setSelectedStage(businessProcessFlows.flows[0].currentStage);
      }

      const normalizedFlows = flowsDataNormalizer(businessProcessFlows);
      setFlowsData(normalizedFlows);

      Promise.all([
        fetchDepartments({ variables: { input: { accountId } } }),
        fetchTemplates({ variables: { input: { accountId } } }),
      ])
        .then(([{ data: departmentsRes }, { data: templatesRes }]) => {
          if (departmentsRes && templatesRes) {
            if (!selectedDepartment) setDepartment(departmentsRes.departments[0]?.id);

            const normalizedDepartments = departmentsDataNormalizer(departmentsRes);
            setDepartmentsData(normalizedDepartments);

            const normalizedTemplates = templatesDataNormalizer(templatesRes, accountId);
            setTemplatesData(normalizedTemplates);
          }
        })
        .catch(error => {
          logError(error);
          showNotification('Something went wrong', { type: 'error' });
        })
        .finally(() => {
          setGenericLoading(false);
        });
    },
  });

  const handleUpdateBusinessProcessFlows = async (stageId: string) => {
    setViewedStage(null);
    setNotConfirmedSelectedStage(stageId);

    try {
      if (!VOTemplatesAndStagesResponse?.vendorOpportunity?.businessProcessFlows?.flows?.length) return;

      const updateDto = updateBusinessProcessFlowsDtoNormalizer({
        VOId: id,
        stageId,
        selectedFlow,
        VOTemplatesAndStagesResponse,
      });

      const { data: checkedVariables, success, error } = UpdateBPFVariablesSchema.safeParse(updateDto);

      if (success) {
        await onUpdateBusinessProcessFlows({ variables: checkedVariables });
        setSelectedStage(stageId);
        showNotification('Updated successfully');
      } else {
        showNotification('Something went wrong', { type: 'error' });
        logError('zod parse error', error);
      }
    } catch (error) {
      logError(error);
      showNotification('Something went wrong', { type: 'error' });
    } finally {
      setNotConfirmedSelectedStage(null);
    }
  };

  const handleUpdateCompletedStatus = async (stageId: string, nextStatus: boolean) => {
    try {
      console.log('handleUpdateCompletedStatus', stageId, nextStatus);
    } catch (error) {
      logError(error);
    }
  };

  const handleCreateTemplate = () => {
    console.log('handleCreateTemplate');
  };

  const handleViewStage = (stage: Stage) => {
    setViewedStage(prev => (prev === stage.businessProcessFlowStageId ? null : stage.businessProcessFlowStageId));
  };

  const handleSelectDepartment = (departmentId: DepartmentId) => {
    setViewedStage(null);
    setDepartment(departmentId);
    setFlow(templatesData[departmentId]?.businessProcessFlowId ?? null);
    console.log(flowsData[templatesData[departmentId]?.businessProcessFlowId]);
    setSelectedStage(flowsData[templatesData[departmentId]?.businessProcessFlowId]);
  };

  const attributeRenderers: AttributeRenderers = {
    checklist: attribute => (
      <Checklist
        checklist={attribute}
        isLoading={isStagesUpdating}
        onChange={async (checklistItem, nextValue) => {
          if (!selectedFlow || !selectedStage || !selectedDepartment) return;

          const currentStagesFields = templatesData[selectedDepartment].stages[selectedStage].stageFields;

          await handleUpdateStageAttributes({
            businessProcessFlowStageId: selectedStage,
            stageFields: currentStagesFields.map(field => {
              if (field.field === 'checklist') {
                return {
                  ...field,
                  value: field.value.map(fieldValue => {
                    if (fieldValue.name === checklistItem.name) {
                      return { ...fieldValue, value: nextValue };
                    }

                    return fieldValue;
                  }),
                };
              }

              return field;
            }),
          });
        }}
      />
    ),
    notes: attribute => (
      <Notes
        note={attribute}
        isLoading={isStagesUpdating}
        onChange={async nextValue => {
          if (!selectedFlow || !selectedStage || !selectedDepartment) return;

          const currentStagesFields = templatesData[selectedDepartment].stages[selectedStage].stageFields;

          await handleUpdateStageAttributes({
            businessProcessFlowStageId: selectedStage,
            stageFields: currentStagesFields.map(field => {
              if (field.field === 'notes' && field.name === attribute.name) {
                return { ...field, value: nextValue };
              }

              return field;
            }),
          });
        }}
      />
    ),
  };

  return (
    <>
      {children({
        selectedStage,
        stagesOptions: Object.entries(templatesData?.[selectedDepartment!]?.stages ?? {}).map(([stageId, stage]) => ({
          value: stageId,
          label: stage.name,
        })),
        attributeRenderers,
        actions: {
          onViewStage: handleViewStage,
          onCreateTemplate: handleCreateTemplate,
          onSelectDepartment: handleSelectDepartment,
          onSelectStage: handleUpdateBusinessProcessFlows,
          onUpdateCompletedStageStatus: handleUpdateCompletedStatus,
        },
        viewedStage,
        selectedDepartment,
        templates: templatesData,
        notConfirmedSelectedStage,
        departments: departmentsData,
        loading: {
          genericLoading,
          isStagesUpdating,
          isStagesLoading: loading || isDepartmentsLoading || isTemplatesLoading,
          isBPFsUpdating: isBPFsUpdating || networkStatus === NetworkStatus.refetch,
        },
      })}
    </>
  );
};
